import { Component, Input, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { IonInput, ModalController } from '@ionic/angular';
import { IAccount } from 'src/app/models/account.interface';
import { IGoogleSearchResult } from 'src/app/models/google/search-result.interface';
import { IUser } from 'src/app/models/user.interface';
import { AccountService } from 'src/app/services/account.service';
import { AuthService } from 'src/app/services/guard/auth.service';
import { ScrapeLogsService } from 'src/app/services/scrape-logs.service';

@Component({
  selector: 'app-suggestion-modal',
  templateUrl: './suggestion-modal.component.html',
  styleUrls: ['./suggestion-modal.component.scss'],
  standalone: false
})
export class SuggestionComponent implements OnInit, AfterViewInit {

  constructor(private modalController: ModalController,
    private accountService: AccountService,
    private authenticationService: AuthService,
    private scrapeLogService: ScrapeLogsService) { }

  @Input()
  website: string = '';
  @Input()
  websiteTitle: string = '';
  @Input()
  directions: string = '';
  @Input()
  maxParagraphs: number = 5;
  @Input()
  responseTone: string = 'objective';

  @ViewChild('websiteInput', { static: true }) websiteInput: IonInput;

  user: IUser | null;
  account: IAccount;

  websiteReliabilityStatus: string = '...';
  suggestions: IGoogleSearchResult[] = [];

  ngOnInit() {
    this.authenticationService.getUser().then((user: IUser | null) => {
      this.user = user;

      this.account = this.accountService.getActiveAccount();

      // Auto-select the default tone if it's defined in the user data
      if (this.account?.defaultTone) {
        this.responseTone = this.account.defaultTone;
      }

      this.checkWebsiteReliability();
    });
  }

  ngAfterViewInit() {
    // Delay to ensure modal animation completes
    setTimeout(() => {
      this.websiteInput?.setFocus();
    }, 250);
  }


  /** Check the website reliability by calling the backend API */
  checkWebsiteReliability() {
    if (!this.website) {
      this.websiteReliabilityStatus = '';
      return;
    }
    // Replace '/api/scrape-log/reliability' with the actual endpoint if different.
    this.scrapeLogService.getWebsiteReliability(this.website, this.websiteTitle)
      .subscribe({
        next: (data: any) => {
          const rating: number = data.rating;
          if (rating === -1) {
            this.websiteReliabilityStatus = 'unknown';
          }
          else if (rating >= 0.9) {
            this.websiteReliabilityStatus = 'excellent';
          } else if (rating >= 0.75) {
            this.websiteReliabilityStatus = 'okay';
          } else if (rating >= 0.5) {
            this.websiteReliabilityStatus = 'warning';
          } else {
            this.websiteReliabilityStatus = 'problem';
          }

          // If suggestions are returned, update the suggestions array.
          this.suggestions = data.suggestions || [];
        },
        error: (e: any) => {
          const error = e as Error;
          this.websiteReliabilityStatus = 'unknown';
          console.error('Error checking website reliability:', error.message);
        }
      });
  }


  /** When the user selects a suggestion, set the website to that suggestion's link */
  selectSuggestion(suggestion: IGoogleSearchResult) {
    this.website = suggestion.link;
    // Optionally clear the suggestions list once a selection is made.
    this.suggestions = [];
    // Optionally re-check reliability for the new website.
    this.checkWebsiteReliability();
  }

  /** Map reliability status to an Ionic color */
  getReliabilityColor(status: string): string {
    switch (status) {
      case 'excellent': return 'success';
      case 'okay': return 'primary';
      case 'warning': return 'warning';
      case 'problem': return 'danger';
      default: return 'medium';
    }
  }

  /** Resolve the themed AI summarize icon used in the modal title. */
  getAiSummarizeIconSrc(): string {
    return document.documentElement.classList.contains('ion-palette-dark')
      ? 'assets/icon/menu/dark/ai-summarize.svg'
      : 'assets/icon/menu/light/ai-summarize.svg';
  }


  /** Close */
  async dismissModal() {
    await this.modalController.dismiss();
  }

  /** Submit */
  submit() {
    if (!this.user) {
      console.error('Authenticated user not found.');
      this.dismissModal();
      return;
    }
    if (!this.website) {
      console.error('Website URL is required.');
      return;
    }
    this.modalController.dismiss({
      website: this.website,
      maxParagraphs: this.maxParagraphs,
      directions: this.directions,
      tone: this.responseTone
    }, 'ok');
  }
}
